'use client';

// Imperative share-modal store. Mounts a single `<GlobalShareModal />` at
// the AppShell root; any code path can call `openShareModal({ surface, ... })`
// to pop the full ShareModal — including ephemeral surfaces like the
// post-trade success toast where a trigger-mounted ShareModal would
// unmount before the user could interact with it.

import type { StoreApi, UseBoundStore } from 'zustand';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

import type { ShareModalProps } from '@/lib/share';

/** ShareModalProps minus the trigger + controlled-state knobs the global
 *  modal owns. Everything else (surface, url, text, etc.) flows through. */
export type ShareModalRequest = Omit<ShareModalProps, 'trigger' | 'open' | 'onOpenChange'>;

interface ShareModalState {
  open: boolean;
  request: ShareModalRequest | null;
  openShareModal: (request: ShareModalRequest) => void;
  closeShareModal: () => void;
}

export const useShareModalStore: UseBoundStore<StoreApi<ShareModalState>> =
  createTrackedStore<ShareModalState>('useShareModalStore')((set) => ({
    open: false,
    request: null,
    openShareModal(request: ShareModalRequest): void {
      set({ open: true, request });
    },
    closeShareModal(): void {
      // Keep `request` populated through the close animation so the dialog
      // body doesn't blank during exit; the next open replaces it.
      set({ open: false });
    },
  }));
